import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, CheckCircle2, User, Filter } from "lucide-react";
import { toast } from "sonner";
import { api, loadAuthToken, setAuthToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

const STATUS_BADGE = {
  "Received": "bg-slate-100 text-slate-800",
  "Documents Checking": "bg-yellow-100 text-yellow-900",
  "In Process": "bg-blue-100 text-blue-800",
  "Completed": "bg-green-100 text-green-800",
  "Rejected": "bg-red-100 text-red-800",
  "Transferred": "bg-purple-100 text-purple-800",
  "Assigned": "bg-indigo-100 text-indigo-800",
};

const CentralWorkQueuePage = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [source, setSource] = useState("all");
  const [bucket, setBucket] = useState("all");
  const [q, setQ] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const tk = loadAuthToken();
    if (!tk) { navigate("/admin/login"); return; }
    const stored = localStorage.getItem("ap_admin_user");
    if (stored) setMe(JSON.parse(stored));
    load();
    // eslint-disable-next-line
  }, []);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [source, bucket, q]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (source !== "all") params.source = source;
      if (bucket !== "all") params.bucket = bucket;
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/central/queue", { params });
      setLeads(r.data || []);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin/login");
      else toast.error(err?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const claim = async (lead) => {
    try {
      const r = await api.post(`/central/leads/${lead.id}/claim`);
      setLeads((prev) => prev.map((l) => l.id === lead.id ? r.data : l));
      toast.success("Claimed — the request is now yours");
    } catch (err) { toast.error(err?.response?.data?.detail || "Claim failed"); }
  };

  const logout = () => { setAuthToken(null); localStorage.removeItem("ap_admin_user"); navigate("/admin/login"); };

  return (
    <div data-testid="central-queue-page" className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.14em] text-teal-700 font-semibold">Central Office</div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-heading font-semibold text-slate-900">Work Queue</h1>
          {me && <div className="text-sm text-slate-500">{me.name} · {me.email}</div>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/admin/customers")} data-testid="central-nav-customers-btn">Customer Details</Button>
          <Button variant="outline" onClick={logout} data-testid="central-logout-btn"><LogOut className="w-4 h-4 mr-2" /> Sign out</Button>
        </div>
      </div>

      <div className="rounded-lg bg-white border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-[180px]" data-testid="filter-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="website">Website</SelectItem>
              <SelectItem value="branch">Branch</SelectItem>
            </SelectContent>
          </Select>
          <Select value={bucket} onValueChange={setBucket}>
            <SelectTrigger className="w-[200px]" data-testid="filter-bucket"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All requests</SelectItem>
              <SelectItem value="new">New (unclaimed)</SelectItem>
              <SelectItem value="mine">My Claimed</SelectItem>
              <SelectItem value="claimed">All Claimed</SelectItem>
              <SelectItem value="working">Working</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, mobile, ID…" className="flex-1 min-w-[220px]" data-testid="search-input" />
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
      </div>

      <div className="rounded-lg bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Tracking ID</th>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Mobile</th>
                <th className="px-4 py-3 font-medium">Service</th>
                <th className="px-4 py-3 font-medium">Source</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Claimed by</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>}
              {!loading && leads.length === 0 && <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-500">Queue is empty.</td></tr>}
              {!loading && leads.map((l) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => navigate(`/workspace/${l.id}`)} data-testid={`queue-row-${l.request_id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{l.request_id}{l.need_info?.active && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />}</td>
                  <td className="px-4 py-3">{l.name}</td>
                  <td className="px-4 py-3">{l.mobile}</td>
                  <td className="px-4 py-3">{l.service_name}</td>
                  <td className="px-4 py-3">
                    <Badge className={l.source === "branch" ? "bg-purple-100 text-purple-800" : "bg-teal-100 text-teal-800"}>
                      {l.source === "branch" ? `Branch${l.source_branch_name ? " · " + l.source_branch_name : ""}` : "Website"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3"><Badge className={STATUS_BADGE[l.status]}>{l.status}</Badge></td>
                  <td className="px-4 py-3 text-slate-700">
                    {l.claimed_by_name ? <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{l.claimed_by_name}</span> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {!l.claimed_by_user_id ? (
                      <Button size="sm" onClick={() => claim(l)} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid={`claim-${l.request_id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Claim
                      </Button>
                    ) : (
                      <button className="text-teal-700 hover:underline text-xs" onClick={() => navigate(`/workspace/${l.id}`)} data-testid={`open-ws-${l.request_id}`}>Open Workspace →</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CentralWorkQueuePage;
